import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Stack,
  alpha,
  useTheme,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Alert,
  Divider,
  Snackbar,
} from '@mui/material';
import {
  OpenInNew as OpenIcon,
  Refresh as RefreshIcon,
  MergeType as MergeIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import duplicateWineService, {
  DuplicateConfidence,
  DuplicateGroup,
  DuplicateSummary,
  DuplicateWineItem,
  MergePreflightResponse,
} from '../../../services/duplicateWineService';

// ── Confidence badge ──────────────────────────────────────────────────────────

const CONF_COLOR: Record<DuplicateConfidence, 'error' | 'warning'> = {
  exact: 'error',
  high:  'warning',
};
const CONF_LABEL: Record<DuplicateConfidence, string> = {
  exact: 'Exact',
  high:  'Likely',
};

// ── Summary chips ─────────────────────────────────────────────────────────────

interface SummaryBarProps {
  summary:      DuplicateSummary;
  activeFilter: DuplicateConfidence | undefined;
  onFilter:     (c: DuplicateConfidence | undefined) => void;
}
const SummaryBar: React.FC<SummaryBarProps> = ({ summary, activeFilter, onFilter }) => {
  const theme = useTheme();
  const chips: { label: string; value: DuplicateConfidence | undefined; count: number }[] = [
    { label: 'All',   value: undefined, count: summary.total },
    { label: 'Exact', value: 'exact',   count: summary.exact },
    { label: 'Likely',value: 'high',    count: summary.high  },
  ];
  return (
    <Stack direction="row" spacing={1}>
      {chips.map(({ label, value, count }) => {
        const active = activeFilter === value;
        return (
          <Chip
            key={label}
            label={`${label} ${count}`}
            onClick={() => onFilter(value)}
            variant={active ? 'filled' : 'outlined'}
            color={active ? (value ? CONF_COLOR[value] : 'primary') : 'default'}
            sx={{ fontWeight: active ? 700 : 500, cursor: 'pointer', transition: 'all 0.15s',
              '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.08) } }}
          />
        );
      })}
    </Stack>
  );
};

// ── Merge dialog ──────────────────────────────────────────────────────────────

type MergeStep = 'select' | 'preflight' | 'executing';

interface MergeDialogProps {
  group:    DuplicateGroup;
  onClose:  () => void;
  onMerged: () => void;
}

const MergeDialog: React.FC<MergeDialogProps> = ({ group, onClose, onMerged }) => {
  const theme = useTheme();

  const [step, setStep]             = useState<MergeStep>('select');
  const [targetId, setTargetId]     = useState<string>(group.wines[0].id);
  const [sourceId, setSourceId]     = useState<string>(
    group.wines.length === 2 ? group.wines[1].id : ''
  );
  const [preflight, setPreflight]   = useState<MergePreflightResponse | null>(null);
  const [pfLoading, setPfLoading]   = useState(false);
  const [exLoading, setExLoading]   = useState(false);
  const [error, setError]           = useState<string | null>(null);

  // Auto-update source when target changes (2-wine groups)
  const handleTargetChange = (newTargetId: string) => {
    setTargetId(newTargetId);
    if (group.wines.length === 2) {
      setSourceId(group.wines.find(w => w.id !== newTargetId)!.id);
    }
    setPreflight(null);
    setError(null);
  };

  const handleSourceChange = (newSourceId: string) => {
    setSourceId(newSourceId);
    setPreflight(null);
    setError(null);
  };

  const handleRunPreflight = async () => {
    setError(null);
    setPfLoading(true);
    try {
      const result = await duplicateWineService.preflight(sourceId, targetId);
      setPreflight(result);
      setStep('preflight');
    } catch (e: any) {
      setError(e?.response?.data?.error?.message ?? 'Preflight failed. Please try again.');
    } finally {
      setPfLoading(false);
    }
  };

  const handleExecute = async () => {
    setError(null);
    setExLoading(true);
    setStep('executing');
    try {
      await duplicateWineService.execute(sourceId, targetId);
      onMerged();
    } catch (e: any) {
      const msg = e?.response?.data?.error?.message ?? 'Merge failed. No changes were applied.';
      setError(msg);
      setStep('preflight'); // stay on preflight so user can see the error
    } finally {
      setExLoading(false);
    }
  };

  const targetWine  = group.wines.find(w => w.id === targetId);
  const sourceWine  = group.wines.find(w => w.id === sourceId);
  const sourceOpts  = group.wines.filter(w => w.id !== targetId);
  const canRunPf    = !!sourceId && sourceId !== targetId;

  return (
    <Dialog open maxWidth="sm" fullWidth onClose={onClose}>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight={700}>Merge Wines</Typography>
        <Typography variant="body2" color="text.secondary">
          Select which wine to keep. The other will be deactivated.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {/* ── Step: select ── */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <FormLabel sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
            Keep this wine (target)
          </FormLabel>
          <RadioGroup value={targetId} onChange={e => handleTargetChange(e.target.value)}>
            {group.wines.map(wine => (
              <FormControlLabel
                key={wine.id}
                value={wine.id}
                control={<Radio size="small" />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={500}>{wine.displayName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {wine.producerName ? `${wine.producerName} · ` : ''}
                      Created {new Date(wine.createdAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                }
                sx={{ mb: 0.5, alignItems: 'flex-start', '& .MuiRadio-root': { pt: 0.5 } }}
              />
            ))}
          </RadioGroup>
        </FormControl>

        {/* Source picker — only shown for 3+ wine groups */}
        {group.wines.length > 2 && (
          <>
            <Divider sx={{ my: 2 }} />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <FormLabel sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                Merge this wine into the target (source)
              </FormLabel>
              <RadioGroup value={sourceId} onChange={e => handleSourceChange(e.target.value)}>
                {sourceOpts.map(wine => (
                  <FormControlLabel
                    key={wine.id}
                    value={wine.id}
                    control={<Radio size="small" />}
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={500}>{wine.displayName}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Created {new Date(wine.createdAt).toLocaleDateString()}
                        </Typography>
                      </Box>
                    }
                    sx={{ mb: 0.5, alignItems: 'flex-start', '& .MuiRadio-root': { pt: 0.5 } }}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </>
        )}

        {/* ── Step: preflight results ── */}
        {step !== 'select' && preflight && (
          <>
            <Divider sx={{ my: 2 }} />

            {preflight.canProceed ? (
              <Alert severity="success" sx={{ mb: 2 }}>Ready to merge — no blockers.</Alert>
            ) : (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={600} gutterBottom>Cannot proceed:</Typography>
                {preflight.blockers.map((b, i) => (
                  <Typography key={i} variant="body2">• {b}</Typography>
                ))}
              </Alert>
            )}

            <Typography variant="caption" fontWeight={700} color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Inventory Items
            </Typography>
            <Stack spacing={0.25} sx={{ mt: 0.5, mb: 1.5, pl: 1 }}>
              <PreflightRow label="With stock (blocks merge)" value={preflight.inventoryItems.withBalance} warn={preflight.inventoryItems.withBalance > 0} />
              <PreflightRow label="Zero balance — will re-link" value={preflight.inventoryItems.toRelink} />
              <PreflightRow label="Zero balance — will deactivate" value={preflight.inventoryItems.toDeactivate} />
            </Stack>

            <Typography variant="caption" fontWeight={700} color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Wine List Items
            </Typography>
            <Stack spacing={0.25} sx={{ mt: 0.5, mb: 1.5, pl: 1 }}>
              <PreflightRow label="Total affected" value={preflight.wineListItems.total} />
              <PreflightRow label="Will re-link" value={preflight.wineListItems.linkedToRelinkedItem + preflight.wineListItems.unlinked} />
              <PreflightRow label="Will deactivate" value={preflight.wineListItems.linkedToDeactivatedItem + preflight.wineListItems.unlinkedCollision} />
            </Stack>

            <Alert severity="warning" icon={<WarningIcon fontSize="small" />} sx={{ mt: 1 }}>
              <Typography variant="body2">
                <strong>{sourceWine?.displayName}</strong> will be permanently deactivated.
                Stock movements are preserved but not rewritten.
                Some inventory items and wine list entries may be deactivated.
              </Typography>
            </Alert>
          </>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={exLoading} color="inherit">Cancel</Button>

        {step === 'select' ? (
          <Button
            variant="outlined"
            onClick={handleRunPreflight}
            disabled={!canRunPf || pfLoading}
            startIcon={pfLoading ? <CircularProgress size={14} /> : undefined}
          >
            {pfLoading ? 'Checking…' : 'Check merge'}
          </Button>
        ) : (
          <>
            <Button variant="outlined" onClick={() => { setStep('select'); setPreflight(null); setError(null); }}
              disabled={exLoading}>
              Back
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleExecute}
              disabled={!preflight?.canProceed || exLoading}
              startIcon={exLoading ? <CircularProgress size={14} color="inherit" /> : <MergeIcon />}
            >
              {exLoading ? 'Merging…' : 'Confirm merge'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

// Small helper row for preflight numbers
const PreflightRow: React.FC<{ label: string; value: number; warn?: boolean }> = ({ label, value, warn }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" fontWeight={600} color={warn && value > 0 ? 'error.main' : 'text.primary'}>
      {value}
    </Typography>
  </Box>
);

// ── Group card ────────────────────────────────────────────────────────────────

interface GroupCardProps {
  group:    DuplicateGroup;
  onOpen:   (id: string) => void;
  onMerge:  (group: DuplicateGroup) => void;
}

const GroupCard: React.FC<GroupCardProps> = ({ group, onOpen, onMerge }) => {
  const theme = useTheme();
  return (
    <Paper elevation={0} sx={{
      mb: 2,
      border: `1px solid ${alpha(group.confidence === 'exact' ? theme.palette.error.main : theme.palette.warning.main, 0.25)}`,
      borderRadius: 2,
      overflow: 'hidden',
    }}>
      <Box sx={{
        px: 2, py: 1,
        display: 'flex', alignItems: 'center', gap: 1.5,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        backgroundColor: alpha(group.confidence === 'exact' ? theme.palette.error.main : theme.palette.warning.main, 0.04),
      }}>
        <Chip label={CONF_LABEL[group.confidence]} size="small" color={CONF_COLOR[group.confidence]}
          sx={{ fontWeight: 700, fontSize: '0.7rem' }} />
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
          {group.normalizedKey}
        </Typography>
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">{group.wines.length} records</Typography>
          <Button size="small" variant="outlined" startIcon={<MergeIcon />}
            onClick={() => onMerge(group)}
            sx={{ fontSize: '0.75rem', py: 0.25, px: 1 }}>
            Merge
          </Button>
        </Box>
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Producer</TableCell>
            <TableCell sx={{ fontWeight: 700, width: 100 }}>Vintage</TableCell>
            <TableCell sx={{ fontWeight: 700, width: 90 }}>Review</TableCell>
            <TableCell sx={{ fontWeight: 700, width: 100 }}>Created</TableCell>
            <TableCell sx={{ fontWeight: 700, width: 60 }} align="right" />
          </TableRow>
        </TableHead>
        <TableBody>
          {group.wines.map((wine) => (
            <TableRow key={wine.id} hover>
              <TableCell>
                <Typography variant="body2" fontWeight={500}>{wine.displayName || '(unnamed)'}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary">{wine.producerName ?? '—'}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {wine.vintageType === 'single_vintage' ? wine.vintage ?? '—'
                    : wine.vintageType === 'non_vintage' ? 'NV' : 'MV'}
                </Typography>
              </TableCell>
              <TableCell>
                {wine.needsReview
                  ? <Chip label="Needs review" size="small" color="warning" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
                  : <Typography variant="caption" color="text.disabled">OK</Typography>}
              </TableCell>
              <TableCell>
                <Typography variant="caption" color="text.secondary">
                  {new Date(wine.createdAt).toLocaleDateString()}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Open wine record">
                  <IconButton size="small" onClick={() => onOpen(wine.id)}>
                    <OpenIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────

const DuplicateWines: React.FC = () => {
  const navigate = useNavigate();

  const [loading, setLoading]           = useState(true);
  const [groups, setGroups]             = useState<DuplicateGroup[]>([]);
  const [summary, setSummary]           = useState<DuplicateSummary>({ total: 0, exact: 0, high: 0 });
  const [confidenceFilter, setConfidenceFilter] = useState<DuplicateConfidence | undefined>(undefined);
  const [page, setPage]                 = useState(0);
  const [rowsPerPage, setRowsPerPage]   = useState(25);
  const [totalGroups, setTotalGroups]   = useState(0);

  // Merge state
  const [mergeGroup, setMergeGroup]     = useState<DuplicateGroup | null>(null);
  const [snackbar, setSnackbar]         = useState<string | null>(null);

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const data = await duplicateWineService.getGroups(confidenceFilter, page + 1, rowsPerPage);
      setGroups(data.groups);
      setSummary(data.summary);
      setTotalGroups(data.pagination.total);
    } catch (err) {
      console.error('Failed to fetch duplicate wines:', err);
    } finally {
      setLoading(false);
    }
  }, [confidenceFilter, page, rowsPerPage]);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  const handleFilterChange = (c: DuplicateConfidence | undefined) => {
    setConfidenceFilter(c);
    setPage(0);
  };

  const handleOpen = useCallback((id: string) => {
    navigate(`/admin/wines/edit/${id}`);
  }, [navigate]);

  const handleMerged = useCallback(() => {
    setMergeGroup(null);
    setSnackbar('Merge completed successfully.');
    fetchGroups();
  }, [fetchGroups]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>Duplicate Wines</Typography>
          <Typography variant="body2" color="text.secondary">
            Wines that appear to be duplicates based on producer and name matching.
            Open records to review or use Merge to consolidate.
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchGroups} disabled={loading}><RefreshIcon /></IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ mb: 3 }}>
        <SummaryBar summary={summary} activeFilter={confidenceFilter} onFilter={handleFilterChange} />
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : groups.length === 0 ? (
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: 2,
          border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          <Typography variant="body1" color="text.secondary">No duplicate groups found.</Typography>
        </Paper>
      ) : (
        <>
          {groups.map((group, i) => (
            <GroupCard
              key={`${group.confidence}-${group.normalizedKey}-${i}`}
              group={group}
              onOpen={handleOpen}
              onMerge={setMergeGroup}
            />
          ))}
          <TablePagination
            component="div"
            count={totalGroups}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </>
      )}

      {mergeGroup && (
        <MergeDialog
          group={mergeGroup}
          onClose={() => setMergeGroup(null)}
          onMerged={handleMerged}
        />
      )}

      <Snackbar
        open={!!snackbar}
        autoHideDuration={5000}
        onClose={() => setSnackbar(null)}
        message={snackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default DuplicateWines;
